import { Link } from "react-router-dom";
import { Users, DoorOpen, CalendarDays, Footprints, UserPlus, KanbanSquare, LineChart, FileText, Building2, LogOut, Share } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import MobileShell, { LocationSelect } from "./MobileShell";

const STAFF = ["admin", "regional", "community"];
const QUICK = [
  { to: "/m/members", label: "Find a member", sub: "Contacts, offices, WiFi", icon: Users, roles: STAFF },
  { to: "/m/doors", label: "Unlock a door", sub: "One tap", icon: DoorOpen, roles: STAFF },
  { to: "/m/today", label: "Today", sub: "Rooms & day passes", icon: CalendarDays, roles: STAFF },
  { to: "/m/tours", label: "Tours", sub: "Mark showed / no-show", icon: Footprints, roles: STAFF },
  { to: "/m/lead", label: "New lead", sub: "Add in seconds", icon: UserPlus, roles: STAFF },
  { to: "/m/availability", label: "Availability", sub: "Open offices & prices", icon: Building2, roles: STAFF },
];
const MORE = [
  { to: "/crm", label: "CRM pipeline", icon: KanbanSquare, roles: STAFF },
  { to: "/", label: "Financials", icon: LineChart, roles: ["admin", "regional", "investor"] },
  { to: "/proposals", label: "Proposals", icon: FileText, roles: STAFF },
];

const standalone = () => window.navigator.standalone || window.matchMedia?.("(display-mode: standalone)").matches;

export default function MobileHome() {
  const { user, logout } = useAuth();
  const can = (t) => t.roles.includes(user?.role);
  const first = (user?.name || "").split(" ")[0];
  return (
    <MobileShell title={first ? `Hi, ${first}` : "Bond Hub"} back={null}>
      {QUICK.some(can) && <LocationSelect />}
      <div className="grid grid-cols-2 gap-3" data-testid="m-quick">
        {QUICK.filter(can).map((t, i) => (
          <Link key={t.to} to={t.to} data-testid={`m-tile-${t.to.split("/").pop()}`}
            className="bg-brand-surface border border-brand-border rounded-2xl p-4 min-h-[120px] flex flex-col justify-between active:scale-[0.97] active:bg-brand-hover transition-transform animate-in fade-in slide-in-from-bottom-2"
            style={{ animationDelay: `${i * 40}ms`, animationFillMode: "both" }}>
            <t.icon className="h-7 w-7 text-brand-ink" strokeWidth={1.5} />
            <span>
              <span className="block text-base font-medium leading-tight">{t.label}</span>
              <span className="block text-xs text-brand-tertiary mt-0.5">{t.sub}</span>
            </span>
          </Link>
        ))}
      </div>
      {MORE.some(can) && (
        <div className="bg-brand-surface border border-brand-border rounded-2xl divide-y divide-brand-border" data-testid="m-more">
          {MORE.filter(can).map((t) => (
            <Link key={t.to} to={t.to} className="flex items-center gap-3 px-4 py-4 active:bg-brand-hover" data-testid={`m-more-${t.label.split(" ")[0].toLowerCase()}`}>
              <t.icon className="h-5 w-5 text-brand-muted" strokeWidth={1.5} />
              <span className="flex-1 text-base">{t.label}</span>
              <span className="text-xs text-brand-tertiary">Full Hub →</span>
            </Link>
          ))}
        </div>
      )}
      {!standalone() && (
        <div className="border border-dashed border-brand-border rounded-2xl p-4 text-sm text-brand-muted" data-testid="m-install-tip">
          <p className="font-medium text-brand-ink mb-1">Put the Hub on your home screen</p>
          In Safari tap <Share className="inline h-4 w-4 -mt-1" /> <b>Share</b>, then <b>Add to Home Screen</b>. It opens straight to this screen, full-screen like an app.
        </div>
      )}
      <button onClick={logout} className="w-full flex items-center justify-center gap-2 text-sm text-brand-tertiary py-3" data-testid="m-logout"><LogOut className="h-4 w-4" /> Sign out</button>
    </MobileShell>
  );
}
